import * as React from 'react'
import { useIssuingElementsStripe } from '../../../../externalServices/stripe/useLoadStripe'
import { AliceCardFragment } from '@alice-financial/alice-sdk'
import { useIssuingElementsConfig } from './useIssuingElementsConfig'
import { PRIMARY_FONT_FAMILY } from '@alice-financial/pretext-ui'

const CARD_DETAIL_STYLE = {
  base: {
    color: 'rgba(0, 0, 0, 0.75)',
    fontSize: '16px',
    fontFamily: `'${PRIMARY_FONT_FAMILY}', -apple-system, sans-serif`,
  },
}

type IssuingElementsData = {
  stripeAccount: string
  card: AliceCardFragment
  onClickNumberCopy?: () => void
  onClickCvcCopy?: () => void
}
export const useIssuingElements = ({
  stripeAccount,
  card,
  onClickNumberCopy,
  onClickCvcCopy,
}: IssuingElementsData) => {
  const issuingElementsStripe = useIssuingElementsStripe({ stripeAccount })
  const issuingConfig = useIssuingElementsConfig(card, issuingElementsStripe)
  return React.useMemo(() => {
    if (!issuingConfig || !issuingConfig.issuingCard) return null
    const config = { ...issuingConfig, ...{ style: CARD_DETAIL_STYLE } }
    const getElements = issuingElementsStripe.then(({ elements }) => elements)

    return {
      mountNumber: getElements
        .then((elements) => elements.create('issuingCardNumberDisplay', config))
        .then((element) => element.mount),
      mountExpiry: getElements
        .then((elements) => elements.create('issuingCardExpiryDisplay', config))
        .then((element) => element.mount),
      mountCvc: getElements
        .then((elements) => elements.create('issuingCardCvcDisplay', config))
        .then((element) => element.mount),
      mountNumberCopy: getElements
        .then((elements) =>
          elements.create('issuingCardCopyButton', {
            toCopy: 'number',
            style: { base: { lineHeight: '44px', fontSize: '44px' } }, // 44px is approximately the height of the button
          })
        )
        .then((element) => (domElement: string | HTMLElement) => {
          element.mount(domElement)
          if (onClickNumberCopy) element.on('click', onClickNumberCopy)
        }),
      mountCvcCopy: getElements
        .then((elements) =>
          elements.create('issuingCardCopyButton', {
            toCopy: 'cvc',
            style: { base: { lineHeight: '44px', fontSize: '44px' } }, // 44px is approximately the height of the button
          })
        )
        .then((element) => (domElement: string | HTMLElement) => {
          element.mount(domElement)
          if (onClickCvcCopy) element.on('click', onClickCvcCopy)
        }),
    }
  }, [issuingElementsStripe, issuingConfig, onClickNumberCopy, onClickCvcCopy])
}
